import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, CreditCard, Megaphone, UserPlus, Users, Wallet } from "lucide-react";
import { api } from "../../../services/apiService";
import { useAuth } from "../../../context/AuthContext";
import { Badge } from "../../../components/ops/Badge";
import { EmptyState } from "../../../components/ops/EmptyState";
import { PageHeader } from "../../../components/ops/PageHeader";
import { Sparkbars } from "../../../components/ops/Sparkbars";
import { StatCard } from "../../../components/ops/StatCard";
import { todayLabel } from "../../../utils/greeting";
import { countSince, dailyCounts } from "../../../utils/trends";

const quickActions = [
  { to: "/staff/schedule", label: "Manage schedule", description: "Open sessions, rosters, and attendance", icon: CalendarClock },
  { to: "/staff/enrollments", label: "Review enrollments", description: "Update membership statuses", icon: CreditCard },
  { to: "/staff/leads", label: "Follow up on leads", description: "Move new inquiries through the pipeline", icon: UserPlus },
  { to: "/staff/announcements", label: "Post an announcement", description: "Share news with families", icon: Megaphone },
];

export function StaffDashboardPage() {
  const { user, accessToken } = useAuth();
  const [leads, setLeads] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [checkouts, setCheckouts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    Promise.all([
      api.get("/leads"),
      api.get("/memberships/enrollments"),
      api.get("/checkout/manage/all"),
      api.get("/schedule/sessions", { from: new Date().toISOString() }),
    ])
      .then(([leadsResponse, enrollmentsResponse, checkoutsResponse, sessionsResponse]) => {
        if (!active) return;
        setLeads(leadsResponse.data.leads || []);
        setEnrollments(enrollmentsResponse.data.enrollments || []);
        setCheckouts(checkoutsResponse.data.checkouts || []);
        setSessions(sessionsResponse.data.sessions || []);
      })
      .catch(() => { if (active) setError("Unable to load dashboard data"); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [accessToken]);

  const stats = useMemo(() => {
    const newLeads = leads.filter((lead) => lead.status === "new");
    const activeEnrollments = enrollments.filter((e) => e.status === "active");
    const paidThisMonth = checkouts.filter((c) => {
      if (c.status !== "paid") return false;
      const created = new Date(c.createdAt);
      const now = new Date();
      return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
    });

    const pendingEnrollments = enrollments.filter((e) => e.status === "pending").length;

    return {
      newLeads: newLeads.length,
      totalLeads: leads.length,
      newLeadsThisWeek: countSince(leads),
      activeEnrollments: activeEnrollments.length,
      totalEnrollments: enrollments.length,
      pendingEnrollments,
      revenueThisMonth: paidThisMonth.reduce((sum, c) => sum + (c.plan?.priceCents || 0), 0) / 100,
      upcomingSessions: sessions.length,
    };
  }, [leads, enrollments, checkouts, sessions]);

  const leadsByDay = useMemo(() => dailyCounts(leads), [leads]);
  const recentLeads = useMemo(() => [...leads].slice(0, 5), [leads]);

  return (
    <div>
      <PageHeader title={`Welcome back, ${user.firstName || "there"}`} subtitle={`${todayLabel()} · Front-office operations at a glance`} />

      <div className="ops-stat-grid">
        <StatCard
          icon={UserPlus}
          label="New leads"
          value={loading ? "—" : stats.newLeads}
          hint={`${stats.totalLeads} total`}
          trend={!loading && stats.newLeadsThisWeek > 0 ? { label: `+${stats.newLeadsThisWeek} this week` } : null}
        />
        <StatCard icon={CreditCard} label="Active enrollments" value={loading ? "—" : stats.activeEnrollments} hint={`${stats.totalEnrollments} total`} />
        <StatCard icon={Wallet} label="Revenue this month" value={loading ? "—" : `$${stats.revenueThisMonth.toFixed(2)}`} hint="From completed checkouts" />
        <StatCard icon={CalendarClock} label="Upcoming sessions" value={loading ? "—" : stats.upcomingSessions} hint="Across all coaches" />
      </div>

      {error && <div className="ops-alert ops-alert--error">{error}</div>}

      <div className="ops-columns">
        <div className="ops-card">
          <h2 className="ops-card-title">Recent leads</h2>
          {loading ? (
            <p className="ops-loading">Loading leads…</p>
          ) : recentLeads.length === 0 ? (
            <EmptyState icon={Users} title="No leads yet" description="New inquiries will show up here." />
          ) : (
            <ul className="ops-list">
              {recentLeads.map((lead) => (
                <li key={lead._id} className="ops-list-item">
                  <div className="ops-list-item-avatar">{lead.name?.charAt(0) || "?"}</div>
                  <div className="ops-list-item-body">
                    <p className="ops-list-item-title">{lead.name}</p>
                    <p className="ops-list-item-meta">{lead.email}</p>
                  </div>
                  <Badge tone={lead.status === "new" ? "accent" : "neutral"}>{lead.status}</Badge>
                </li>
              ))}
            </ul>
          )}
          <Link to="/staff/leads" className="ops-card-link">
            View all leads <ArrowRight size={14} />
          </Link>
        </div>

        <div className="ops-stack">
          <div className="ops-card">
            <div className="ops-activity-header">
              <h2 className="ops-card-title" style={{ margin: 0 }}>New leads</h2>
              {!loading && <Sparkbars data={leadsByDay} />}
            </div>
            <p className="ops-activity-total">
              <strong>{loading ? "—" : stats.newLeadsThisWeek}</strong> {stats.newLeadsThisWeek === 1 ? "lead" : "leads"} received in the last 7 days
            </p>

            <ul className="ops-attention-list">
              {!loading && stats.newLeads > 0 && (
                <li>
                  <Link to="/staff/leads" className="ops-attention-item">
                    <span className="ops-attention-item-label">
                      <span className="ops-attention-item-dot" />
                      {stats.newLeads} {stats.newLeads === 1 ? "lead needs" : "leads need"} follow-up
                    </span>
                    <ArrowRight size={14} />
                  </Link>
                </li>
              )}
              {!loading && stats.pendingEnrollments > 0 && (
                <li>
                  <Link to="/staff/enrollments" className="ops-attention-item">
                    <span className="ops-attention-item-label">
                      <span className="ops-attention-item-dot" />
                      {stats.pendingEnrollments} {stats.pendingEnrollments === 1 ? "enrollment" : "enrollments"} pending review
                    </span>
                    <ArrowRight size={14} />
                  </Link>
                </li>
              )}
              {!loading && stats.newLeads === 0 && stats.pendingEnrollments === 0 && (
                <li className="ops-attention-empty">You're all caught up — nothing needs attention.</li>
              )}
            </ul>
          </div>

          <div className="ops-card">
            <h2 className="ops-card-title">Quick actions</h2>
            <div className="ops-action-grid">
              {quickActions.map(({ to, label, description, icon: Icon }) => (
                <Link key={to} to={to} className="ops-action-card">
                  <div className="ops-action-card-icon">
                    <Icon size={20} strokeWidth={1.8} />
                  </div>
                  <div className="ops-action-card-content">
                    <h3>{label}</h3>
                    <p>{description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
